import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiTag, FiEdit3, FiTrash2, FiPlus, FiSearch, FiList, FiGrid } from 'react-icons/fi';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [addType, setAddType] = useState('activity'); // 'activity' or 'subcategory'
  const [newCategory, setNewCategory] = useState({
    activity: '',
    description: '',
    subcategory: '',
    parentActivity: ''
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/business-types`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.data || []);
      } else {
        throw new Error('Failed to load field of activity');
      }
    } catch (err) {
      setError('Failed to load field of activity');
      console.error('Categories fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (addType === 'subcategory') {
        // Add subcategory to selected business type
        if (!newCategory.parentActivity || !newCategory.subcategory) {
          alert('Please select a field of activity and enter a subcategory name');
          return;
        }

        const parentBusiness = categories.find(cat => cat._id === newCategory.parentActivity);
        if (!parentBusiness) {
          alert('Selected field of activity not found');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/business-types/${parentBusiness._id}/subcategories`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ name: newCategory.subcategory })
        });

        if (response.ok) {
          const data = await response.json();
          setCategories(categories.map(cat => 
            cat._id === parentBusiness._id ? data.data : cat
          ));
          setShowAddModal(false);
          setNewCategory({ activity: '', description: '', subcategory: '', parentActivity: '' });
          setAddType('activity');
          alert('Subcategory added successfully');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add subcategory');
        }
      } else {
        // Add new business type
        if (!newCategory.activity) {
          alert('Please enter a field of activity name');
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/business-types`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            activity: newCategory.activity,
            description: newCategory.description
          })
        });

        if (response.ok) {
          const data = await response.json();
          setCategories([...categories, data.data]);
          setShowAddModal(false);
          setNewCategory({ activity: '', description: '', subcategory: '', parentActivity: '' });
          alert('Field of activity added successfully');
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add field of activity');
        }
      }
    } catch (err) {
      alert('Failed to add: ' + err.message);
    }
  };

  const handleEditCategory = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/categories/${selectedCategory._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedCategory)
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(categories.map(cat => 
          cat._id === selectedCategory._id ? data.category : cat
        ));
        setShowEditModal(false);
        setSelectedCategory(null);
        alert('Category updated successfully');
      } else {
        throw new Error('Failed to update category');
      }
    } catch (err) {
      alert('Failed to update category: ' + err.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/management/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setCategories(categories.filter(cat => cat._id !== categoryId));
        alert('Category deleted successfully');
      } else {
        throw new Error('Failed to delete category');
      }
    } catch (err) {
      alert('Failed to delete category: ' + err.message);
    }
  };

  const openEditModal = (category) => {
    setSelectedCategory({ ...category });
    setShowEditModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const filteredCategories = categories.filter(category =>
    searchTerm === '' || 
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPosts = categories.reduce((sum, cat) => sum + cat.postCount, 0);

  return (
    <Container>
      <PageHeader>
        <PageTitle>
          <FiTag /> Category Management
        </PageTitle>
        <CategoryStats>
          <StatItem>
            <StatValue>{categories.length}</StatValue>
            <StatLabel>Total Categories</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{totalPosts}</StatValue>
            <StatLabel>Total Posts</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{categories.filter(c => c.postCount > 0).length}</StatValue>
            <StatLabel>Active Categories</StatLabel>
          </StatItem>
        </CategoryStats>
      </PageHeader>

      <ActionsCard>
        <SearchInput>
          <FiSearch />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <AddButton onClick={() => setShowAddModal(true)}>
          <FiPlus /> Add Field of Activity
        </AddButton>
      </ActionsCard>

      <CategoriesCard>
        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>Loading field of activity...</LoadingText>
          </LoadingContainer>
        ) : error ? (
          <ErrorMessage>{error}</ErrorMessage>
        ) : (
          <CategoriesGrid>
            {filteredCategories.map((category) => (
              <CategoryCard key={category._id}>
                <CategoryHeader>
                  <CategoryIcon>
                    <FiTag />
                  </CategoryIcon>
                  <CategoryActions>
                    <ActionButton 
                      onClick={() => openEditModal(category)}
                      color="#f59e0b"
                      title="Edit Field of Activity"
                    >
                      <FiEdit3 />
                    </ActionButton>
                    <ActionButton 
                      onClick={() => handleDeleteCategory(category._id)}
                      color="#ef4444"
                      title="Delete Field of Activity"
                    >
                      <FiTrash2 />
                    </ActionButton>
                  </CategoryActions>
                </CategoryHeader>

                <CategoryName>{category.activity}</CategoryName>
                <CategoryDescription>{category.description}</CategoryDescription>

                <CategoryMeta>
                  <MetaItem>
                    <strong>Subcategories:</strong> {category.subcategories?.length || 0}
                  </MetaItem>
                  <MetaItem>
                    <strong>Status:</strong> {category.is_active ? 'Active' : 'Inactive'}
                  </MetaItem>
                </CategoryMeta>

                {category.subcategories && category.subcategories.length > 0 && (
                  <SubcategoriesSection>
                    <SubcategoriesTitle>Subcategories:</SubcategoriesTitle>
                    <SubcategoriesList>
                      {category.subcategories.map((sub, index) => (
                        <SubcategoryTag key={index}>
                          {sub.name}
                        </SubcategoryTag>
                      ))}
                    </SubcategoriesList>
                  </SubcategoriesSection>
                )}
              </CategoryCard>
            ))}
          </CategoriesGrid>
        )}
      </CategoriesCard>

      {/* Add Category Modal */}
      {showAddModal && (
        <Modal onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Add Field of Activity</ModalTitle>
              <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>What would you like to add?</FormLabel>
                <RadioGroup>
                  <RadioOption>
                    <input 
                      type="radio" 
                      id="add-activity"
                      name="addType" 
                      value="activity"
                      checked={addType === 'activity'}
                      onChange={(e) => setAddType(e.target.value)}
                    />
                    <label htmlFor="add-activity">New Field of Activity</label>
                  </RadioOption>
                  <RadioOption>
                    <input 
                      type="radio" 
                      id="add-subcategory"
                      name="addType" 
                      value="subcategory"
                      checked={addType === 'subcategory'}
                      onChange={(e) => setAddType(e.target.value)}
                    />
                    <label htmlFor="add-subcategory">Subcategory to existing activity</label>
                  </RadioOption>
                </RadioGroup>
              </FormGroup>

              {addType === 'activity' ? (
                <>
                  <FormGroup>
                    <FormLabel>Field of Activity Name</FormLabel>
                    <FormInput
                      type="text"
                      value={newCategory.activity}
                      onChange={(e) => setNewCategory({...newCategory, activity: e.target.value})}
                      placeholder="Enter field of activity (e.g., Restaurant, Retail Store)"
                    />
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Description</FormLabel>
                    <FormTextarea
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                      placeholder="Enter description for this field of activity"
                      rows="3"
                    />
                  </FormGroup>
                </>
              ) : (
                <>
                  <FormGroup>
                    <FormLabel>Select Existing Field of Activity</FormLabel>
                    <FormSelect
                      value={newCategory.parentActivity}
                      onChange={(e) => setNewCategory({...newCategory, parentActivity: e.target.value})}
                    >
                      <option value="">Choose a field of activity</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.activity}</option>
                      ))}
                    </FormSelect>
                  </FormGroup>
                  <FormGroup>
                    <FormLabel>Subcategory Name</FormLabel>
                    <FormInput
                      type="text"
                      value={newCategory.subcategory}
                      onChange={(e) => setNewCategory({...newCategory, subcategory: e.target.value})}
                      placeholder="Enter subcategory name (e.g., Italian Restaurant, Coffee Shop)"
                    />
                  </FormGroup>
                </>
              )}

              <ModalActions>
                <CancelButton onClick={() => setShowAddModal(false)}>Cancel</CancelButton>
                <SaveButton onClick={handleAddCategory}>
                  {addType === 'activity' ? 'Add Field of Activity' : 'Add Subcategory'}
                </SaveButton>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}

      {/* Edit Category Modal */}
      {showEditModal && selectedCategory && (
        <Modal onClick={() => setShowEditModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Edit Category</ModalTitle>
              <CloseButton onClick={() => setShowEditModal(false)}>×</CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <FormLabel>Category Name</FormLabel>
                <FormInput
                  type="text"
                  value={selectedCategory.name}
                  onChange={(e) => setSelectedCategory({...selectedCategory, name: e.target.value})}
                  placeholder="Enter category name"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Description</FormLabel>
                <FormTextarea
                  value={selectedCategory.description}
                  onChange={(e) => setSelectedCategory({...selectedCategory, description: e.target.value})}
                  placeholder="Enter category description"
                  rows="3"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Color</FormLabel>
                <ColorInput
                  type="color"
                  value={selectedCategory.color}
                  onChange={(e) => setSelectedCategory({...selectedCategory, color: e.target.value})}
                />
              </FormGroup>
              <ModalActions>
                <CancelButton onClick={() => setShowEditModal(false)}>Cancel</CancelButton>
                <SaveButton onClick={handleEditCategory}>Update Category</SaveButton>
              </ModalActions>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 2rem;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CategoryStats = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.25rem;
`;

const ActionsCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchInput = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 1rem;
    color: #666;
    z-index: 1;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.5rem;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.5);
    backdrop-filter: blur(5px);
    font-size: 1rem;
    outline: none;
    transition: all 0.3s ease;

    &:focus {
      border-color: #4f46e5;
      background: rgba(255, 255, 255, 0.8);
    }

    &::placeholder {
      color: #999;
    }
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white;
  border: none;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(79, 70, 229, 0.3);
  }
`;

const CategoriesCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #333;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin: 0;
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: #ef4444;
  font-size: 1.1rem;
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  border-left: 4px solid ${props => props.color};

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 15px 35px rgba(0,0,0,0.1);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CategoryIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
`;

const CategoryActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.color};
    color: white;
    transform: translateY(-2px);
  }
`;

const CategoryName = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const CategoryDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0 0 1rem 0;
`;

const CategoryMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #666;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const CategoryProgress = styled.div`
  margin-top: 1rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 0.5rem;

  &::after {
    content: '';
    display: block;
    width: ${props => props.width}%;
    height: 100%;
    background: ${props => props.color};
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.div`
  font-size: 0.8rem;
  color: #666;
  text-align: center;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 0;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.3rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: #1a1a1a;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #1a1a1a;
  font-size: 0.9rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #4f46e5;
    background: rgba(255, 255, 255, 0.8);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #4f46e5;
    background: rgba(255, 255, 255, 0.8);
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  input[type="radio"] {
    margin: 0;
  }

  label {
    font-size: 0.9rem;
    color: #1a1a1a;
    cursor: pointer;
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.5);
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  resize: vertical;
  font-family: inherit;

  &:focus {
    border-color: #4f46e5;
    background: rgba(255, 255, 255, 0.8);
  }
`;

const FormHelperText = styled.small`
  display: block;
  margin-top: 0.5rem;
  color: #666;
  font-size: 0.8rem;
  line-height: 1.4;
`;

const SubcategoriesSection = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const SubcategoriesTitle = styled.h4`
  margin: 0 0 0.5rem 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a1a;
`;

const SubcategoriesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const SubcategoryTag = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ColorInput = styled.input`
  width: 60px;
  height: 40px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 0;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 8px;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const CancelButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.5);
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.8);
    color: #1a1a1a;
  }
`;

const SaveButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #4f46e5, #7c3aed);
  color: white;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(79, 70, 229, 0.3);
  }
`;

export default Categories;
